import { Link } from "react-router-dom";
import { livePlatforms, comingSoon } from "../../lib/platforms";
import {
  ArrowRight, Keyboard, Building2, Network, ShieldCheck, Gauge, Gift,
  KeyRound, CreditCard, HandCoins, Inbox, Send,
} from "lucide-react";
import { PRICE_LABEL } from "../../content/pricing";

export function SectionHead({ eyebrow, title, children }) {
  return (
    <div className="max-w-2xl">
      <div className="label-mini">{eyebrow}</div>
      <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-3 leading-[1.05]">{title}</h2>
      {children && <p className="text-sm text-neutral-600 mt-4 leading-relaxed">{children}</p>}
    </div>
  );
}


export function WorksWith() {
  return (
    <section className="border-y border-neutral-900 bg-neutral-50" data-testid="home-works-with">
      <div className="max-w-6xl mx-auto px-6 py-6 flex flex-col md:flex-row md:items-center gap-4 md:gap-8">
        <div className="label-mini shrink-0">works with</div>
        <div className="flex flex-wrap gap-2">
          {livePlatforms().map((p) => (
            <span key={p.key} className="bg-white border border-neutral-900 px-3 py-1.5 text-xs font-semibold"
                  data-testid={`works-with-${p.short.toLowerCase().replace(/\s+/g, "-")}`}>
              {p.short}{p.status === "beta" ? " (beta)" : ""}
            </span>
          ))}
          {comingSoon().map((p) => (
            <span key={p.key} className="border border-dashed border-neutral-300 px-3 py-1.5 text-xs text-neutral-400"
                  data-testid={`works-with-${p.key}`}>{p.short} — coming soon</span>
          ))}
        </div>
      </div>
    </section>
  );
}

const BENEFITS = [
  [Keyboard, "No more re-keying", "Invoice lines land in your GL as a bill exactly as the vendor sent them."],
  [Building2, "Keep your own system", "Vendors and customers each stay in the accounting software they already use."],
  [Network, "Clean, mapped data", "Map each vendor item to your GL account once; LF applies it every time."],
  [ShieldCheck, "You stay in control", "Approve new vendors once and see every invoice's status: synced, held or failed."],
  [Gauge, "Fewer errors, faster close", "No typos, no missing tax, no lost PDFs, so month-end goes quicker."],
  [Gift, "Free for vendors", "Sending invoices costs nothing. Only the receiving business pays, and only per invoice."],
];

export function Benefits() {
  return (
    <section className="max-w-6xl mx-auto px-6 py-24" data-testid="home-benefits">
      <SectionHead eyebrow="01 / why lingua franca" title="Bills that arrive ready, not retyped." />
      <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 border-l border-t border-neutral-900">
        {BENEFITS.map(([Icon, title, text], i) => (
          <div key={title} className="group border-r border-b border-neutral-900 p-7 bg-white transition-colors duration-300 hover:bg-neutral-900 hover:text-white"
               data-testid={`benefit-card-${i + 1}`}>
            <div className="w-10 h-10 border border-current flex items-center justify-center">
              <Icon size={18} strokeWidth={2} />
            </div>
            <h3 className="font-display text-lg font-bold mt-6">{title}</h3>
            <p className="text-sm text-neutral-600 group-hover:text-neutral-300 mt-2 leading-relaxed transition-colors duration-300">{text}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

const STEPS = [
  ["Connect", "Connect your accounting system in a couple of clicks. Sign-in happens with your provider; we never see your password."],
  ["Approve and map", "Approve a new vendor once and map their items to your GL accounts."],
  ["Bills appear", "New invoices sync into your accounting system as bills, ready to review and pay the way you do today."],
];

export function HowItWorks() {
  return (
    <section id="how-it-works" className="bg-neutral-50 border-y border-neutral-200 scroll-mt-4" data-testid="home-how-it-works">
      <div className="max-w-6xl mx-auto px-6 py-24">
        <SectionHead eyebrow="02 / how it works" title="Three steps. Then it just runs." />
        <ol className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          {STEPS.map(([title, text], i) => (
            <li key={title} className="relative bg-white border border-neutral-900 p-7" data-testid={`how-step-${i + 1}`}>
              <div className="font-display text-5xl font-extrabold text-neutral-200 leading-none">0{i + 1}</div>
              <h3 className="font-display text-xl font-bold mt-4">{title}</h3>
              <p className="text-sm text-neutral-600 mt-2 leading-relaxed">{text}</p>
            </li>
          ))}
        </ol>
        <Link to="/help#quick-start" className="inline-flex items-center gap-2 mt-8 text-sm font-semibold underline underline-offset-4 hover:no-underline"
              data-testid="how-read-guide">
          Read the full guide <ArrowRight size={14} />
        </Link>
      </div>
    </section>
  );
}

export function WhoFor() {
  const cols = [
    [Send, "vendors", "For vendors sending invoices", "Keep invoicing from your own system. Your customers get structured bills instead of a PDF. Always free."],
    [Inbox, "customers", "For customers receiving invoices", "Stop retyping vendor invoices. Get bills in your GL with the detail your team needs."],
  ];
  return (
    <section className="max-w-6xl mx-auto px-6 py-24" data-testid="home-who-for">
      <SectionHead eyebrow="03 / who it's for" title="Both sides of the invoice win." />
      <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
        {cols.map(([Icon, key, title, text]) => (
          <div key={key} className="border-t-4 border-neutral-900 pt-6" data-testid={`who-for-${key}`}>
            <Icon size={20} strokeWidth={2} />
            <h3 className="font-display text-2xl font-bold mt-4">{title}</h3>
            <p className="text-sm text-neutral-600 mt-3 leading-relaxed max-w-md">{text}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

export function Pricing() {
  return (
    <section id="pricing" className="max-w-6xl mx-auto px-6 pb-24 scroll-mt-4" data-testid="home-pricing">
      <div className="bg-neutral-900 text-white grid grid-cols-1 lg:grid-cols-[1fr_1.2fr]">
        <div className="p-8 sm:p-12 border-b lg:border-b-0 lg:border-r border-neutral-700">
          <div className="label-mini text-neutral-400">04 / pricing</div>
          <div className="mt-6 flex items-end gap-2">
            <span className="font-display text-6xl sm:text-7xl font-extrabold tracking-tight" data-testid="home-price">{PRICE_LABEL}</span>
            <span className="text-sm text-neutral-400 pb-2">per invoice synced</span>
          </div>
          <p className="text-sm text-neutral-400 mt-4">after your free start</p>
        </div>
        <div className="p-8 sm:p-12 space-y-5 text-sm leading-relaxed">
          <p><strong className="font-display text-lg">Free to start:</strong> 6 months or 100 invoices, whichever comes first.</p>
          <p data-testid="home-price-text">Then {PRICE_LABEL} per invoice synced into your accounting system. No monthly fee, no minimum, no contract.</p>
          <p className="font-semibold">Sending invoices is always free.</p>
          <p data-testid="home-pricing-no-card">No credit card needed. Your first 100 invoices (or 6 months) are free, then $0.25 per invoice.</p>
          <p className="text-xs text-neutral-400 border-t border-neutral-700 pt-5">
            Retries, re-syncs and status updates on an invoice that already synced are never charged again. Held or failed invoices aren't charged.
          </p>
          <Link to="/help#pricing" className="inline-flex items-center gap-2 text-xs uppercase tracking-widest underline underline-offset-4 hover:text-neutral-300"
                data-testid="home-pricing-details">
            Pricing details <ArrowRight size={12} />
          </Link>
        </div>
      </div>
    </section>
  );
}

export function Trust() {
  const items = [
    [KeyRound, "We never see your passwords. You sign in directly with your accounting provider."],
    [CreditCard, "We never store card details. Payments are handled by Stripe."],
    [HandCoins, "We never move money or edit invoices."],
  ];
  return (
    <section className="border-t border-neutral-200" data-testid="home-trust">
      <div className="max-w-6xl mx-auto px-6 py-12 flex flex-col lg:flex-row lg:items-center gap-6 lg:gap-10">
        <ul className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-4">
          {items.map(([Icon, text]) => (
            <li key={text} className="flex items-start gap-3 text-sm text-neutral-700">
              <Icon size={16} className="shrink-0 mt-0.5" />{text}
            </li>
          ))}
        </ul>
        <Link to="/help#security" className="inline-flex items-center gap-2 text-xs uppercase tracking-widest underline underline-offset-4 shrink-0"
              data-testid="home-security-link">
          Security <ArrowRight size={12} />
        </Link>
      </div>
    </section>
  );
}

export function FinalCta() {
  return (
    <section className="bg-neutral-900 text-white grid-overlay-dark" data-testid="home-final-cta">
      <div className="max-w-6xl mx-auto px-6 py-24 flex flex-col md:flex-row md:items-end md:justify-between gap-10">
        <h2 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.02] max-w-xl">
          Stop re-typing your vendors' invoices.
        </h2>
        <div className="flex flex-col items-start gap-4">
          <Link to="/register" className="inline-flex items-center gap-2 bg-white text-neutral-900 px-6 py-3 text-xs uppercase tracking-widest font-semibold transition-transform duration-200 hover:-translate-y-0.5"
                data-testid="final-cta-get-started">
            Get started free <ArrowRight size={14} />
          </Link>
          <a href="mailto:support@linguafrancainv.com" className="text-sm text-neutral-400 hover:text-white underline underline-offset-4"
             data-testid="final-cta-support-email">
            Questions? support@linguafrancainv.com
          </a>
        </div>
      </div>
    </section>
  );
}
